import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Grid from '@mui/material/Grid2';
import CommonCard from '../components/common_card';
import { Alert } from '@mui/material';
import { RequestButton } from "../style/button";
import { HowToReg } from '@mui/icons-material';
import { TypographyBold } from '../style/typography';
import { BoxCenterStart, BoxSpaceCenter } from '../style/box';
import { commonStyles } from "../style/common";
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import { useAuth } from '../contexts/AuthContext';
import { validationSchemaUpdateUserInfo } from '../func/validation_check';
import { fetchData } from "../func/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { BaseDialog } from "../components/base_dialog";
import { CustomControlledInput } from "../components/CustomControlledInput";
import { postUserInfo } from "../func/api";
import SkeletonLoading from "../components/skeleton_loading";
import { ROLE_UUID, REPORT_STATUS } from "../const/config";

const MyPage = () => {
    const [users, setUsers] = useState([]);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [fullName, setFullName] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false); // ダイアログ表示制御
    const [formData, setFormData] = useState(null); // ダイアログ経由で送るデータ
    const [earthquakeList, setEarthquakeList] = useState([]);

    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);

    const { control, handleSubmit, reset } = useForm({
        resolver: yupResolver(validationSchemaUpdateUserInfo(users, currentUser.id)),
        defaultValues: {
            updateUserInfo: {
                tel: '',
                email: '',
                password: '',
                confirmPassword: '',
            }
        },
        mode: "onBlur"
    });

    useEffect(() => {
        if (currentUser?.role_id === ROLE_UUID.VIEWER) {
            setIsDisabled(true);
        }
    }, [currentUser]);

    useEffect(() => {
        const getUserInfo = async () => {
            try {
                const userInfo = await fetchData(`api/get_user_info/${currentUser.id}`);
                setFullName(userInfo.full_name || '');

                const userList = await fetchData(`api/get_user_list`);
                setUsers(userList);

                // 業務対応中の場合は、編集不可
                // 地震一覧データ取得
                const earthquakeData = await fetchData(`api/get_earthquake_list/`);
                // 業務対中の場合は、編集不可
                const reportingData = earthquakeData.find(obj =>
                    obj.report_status_uuid === REPORT_STATUS.UNSUPPORTED ||
                    obj.report_status_uuid === REPORT_STATUS.FISRTREPORT ||
                    obj.report_status_uuid === REPORT_STATUS.PRIMARYCHECK ||
                    obj.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTSUPPORT
                );
                setIsDisabled(reportingData ? true : false);

                // フォームに初期値をセット
                reset({
                    updateUserInfo: {
                        tel: userInfo.tel || '',
                        email: userInfo.email || '',
                        password: '',
                        confirmPassword: ''
                    }
                });
            } catch (error) {
                console.error(`ユーザー情報取得中にエラーが発生しました (id: ${currentUser.id}):`, error);
                // 必要に応じてエラー時の処理
                setFullName('');
                reset({
                    updateUserInfo: {
                        tel: '',
                        email: '',
                        password: '',
                        confirmPassword: ''
                    }
                });
            } finally {
                setLoading(false);
            }
        };
        if (currentUser?.id) {
            getUserInfo();
        }
    }, [currentUser, reset]);

    const onSubmit = (data) => {
        // ダイアログに表示して確認
        setFormData(data);
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        setConfirmOpen(false);
        if (!formData) return;

        try {
            // パスワードが入力されていればSHA-256でハッシュ化
            let hashedPassword = formData.updateUserInfo.password;

            if (formData.updateUserInfo.password) {
                const encoder = new TextEncoder();
                const dataBuffer = encoder.encode(formData.updateUserInfo.password);
                const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
                const hashArray = Array.from(new Uint8Array(hashBuffer));
                hashedPassword = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
            }

            await postUserInfo(currentUser.id, formData.updateUserInfo.tel, formData.updateUserInfo.email, hashedPassword);

            setAlertMessage("ユーザー情報の更新が完了しました");
            setAlertSeverity("success");
            setAlertOpen(true);

            // パスワード欄を初期化（空に戻す）
            reset((prev) => ({
                updateUserInfo: {
                    ...prev.updateUserInfo,
                    password: '',
                    confirmPassword: '',
                },
            }));

        } catch (err) {
            console.error("更新失敗しました。：", err);
            setAlertMessage("ユーザー情報の更新に失敗しました。再度時間をおいてからお試しください。");
            setAlertSeverity("error");
            setAlertOpen(true);
        }
    };

    const handleCancel = () => {
        setConfirmOpen(false);
    };

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <BoxCenterStart sx={{ ...commonStyles.page, pb: 2, pt: 2, minHeight: { xs: '100vh', sm: 'auto' } }}>
            <Grid
                container
                spacing={3}
                columns={12}
                sx={{ ...commonStyles.container, height: "auto" }}
                justifyContent={'center'}
            >
                {/* Page Title */}
                <Grid size={{ xs: 12 }}>
                    <CommonCard
                        size={{ xs: 12 }}
                        child={
                            <BoxCenterStart p={1}>
                                <TypographyBold sx={{ fontSize: 25 }}>
                                    ユーザー情報更新
                                </TypographyBold>
                            </BoxCenterStart>
                        }
                    />
                </Grid>

                {/* User Registration */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Grid container spacing={3}>
                            {/* 氏名 */}
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="氏名"
                                    variant="standard"
                                    placeholder="山田太郎"
                                    value={fullName}
                                    InputProps={{
                                        readOnly: true,
                                        disableUnderline: true
                                    }}
                                    disabled={isDisabled}
                                />
                            </Grid>
                            {/* 電話番号 */}
                            <CustomControlledInput
                                name="updateUserInfo.tel"
                                control={control}
                                label="電話番号（ハイフン無し）"
                                placeholder="09012345678"
                                inputProps={{ maxLength: 11, pattern: '[0-9]*' }}
                                onKeyPress={(e) => {
                                    if (!/[0-9]/.test(e.key)) e.preventDefault();
                                }}
                                sx={{ mb: { xs: 1, sm: 0 } }}
                                disabled={isDisabled}
                            />
                            {/* メールアドレス */}
                            <CustomControlledInput
                                name="updateUserInfo.email"
                                control={control}
                                label="メールアドレス"
                                autoComplete="email"
                                placeholder="example@kyuden.co.jp"
                                sx={{ mb: { xs: 1, sm: 0 } }}
                                inputProps={{ maxLength: 255 }}
                                disabled={isDisabled}
                            />
                            {/* パスワード */}
                            <CustomControlledInput
                                name="updateUserInfo.password"
                                control={control}
                                label="パスワード"
                                type="password"
                                autoComplete="new-password"
                                helperText="8文字以上の英大文字・小文字、数字を含むパスワードを設定してください"
                                sx={{ mb: { xs: 1, sm: 0 } }}
                                disabled={isDisabled}
                            />
                            {/* パスワード（確認） */}
                            <CustomControlledInput
                                name="updateUserInfo.confirmPassword"
                                control={control}
                                label="パスワード(確認)"
                                type="password"
                                autoComplete="new-password"
                                helperText="8文字以上の英大文字・小文字、数字を含むパスワードを設定してください"
                                sx={{ mb: { xs: 1, sm: 0 } }}
                                disabled={isDisabled}
                            />
                            {/* 登録ボタン */}
                            <Grid
                                container
                                size={{ xs: 12 }}
                                justifyContent="center"
                                type="Submit"
                            >
                                <Grid size={{ xs: 12 }}>
                                    <BoxSpaceCenter>
                                        <RequestButton
                                            type="submit"
                                            startIcon={<HowToReg />}
                                            disabled={isDisabled}
                                        >
                                            ユーザー情報を更新する
                                        </RequestButton>
                                    </BoxSpaceCenter>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </Grid>
            </Grid>
            {/* 確認ダイアログ */}
            <BaseDialog
                open={confirmOpen}
                onClose={handleCancel}
                title="ユーザー情報更新"
                message="ユーザー情報を更新します。よろしいですか？"
                onConfirm={handleConfirm}
                confirmText='OK'
                cancelText='キャンセル'
            />
            {/* 失敗時のSnackbar */}
            {alertMessage && (
                <Snackbar
                    open={alertOpen}
                    autoHideDuration={3000}
                    onClose={() => setAlertOpen(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                        {alertMessage}
                    </Alert>
                </Snackbar>
            )}
        </BoxCenterStart>
    );
};

export default MyPage;